import { Router } from "express";
import { db } from "@workspace/db";
import {
  tendersTable, tenderDocumentsTable, analysesTable, tenderNotesTable,
  submissionChecklistTable, complianceItemsTable, proposalDraftsTable, companiesTable,
} from "@workspace/db";
import { eq, and, desc, asc, or, sql } from "drizzle-orm";
import { requireAuth, type AuthRequest } from "../lib/auth";
import { getClient } from "../lib/openai.js";
import { generatePdfDocument } from "../lib/export.js";
import { createRequire } from "module";
const _require = createRequire(import.meta.url);
const archiver = (_require("archiver").default ?? _require("archiver")) as typeof import("archiver").default;

const router = Router();

/* ── zero-downtime migrations ─────────────────────────────────────── */
async function ensureFeatureTables() {
  try {
    await db.execute(sql`
      CREATE TABLE IF NOT EXISTS tender_notes (
        id SERIAL PRIMARY KEY,
        tender_id INTEGER NOT NULL,
        user_id INTEGER NOT NULL,
        author_name TEXT NOT NULL,
        content TEXT NOT NULL,
        mentions TEXT[],
        created_at TIMESTAMP NOT NULL DEFAULT NOW(),
        updated_at TIMESTAMP NOT NULL DEFAULT NOW()
      )
    `);
    await db.execute(sql`
      CREATE TABLE IF NOT EXISTS submission_checklist (
        id SERIAL PRIMARY KEY,
        tender_id INTEGER NOT NULL,
        label TEXT NOT NULL,
        label_en TEXT,
        done BOOLEAN NOT NULL DEFAULT FALSE,
        category TEXT DEFAULT 'general',
        sort_order INTEGER NOT NULL DEFAULT 0,
        created_at TIMESTAMP NOT NULL DEFAULT NOW(),
        updated_at TIMESTAMP NOT NULL DEFAULT NOW()
      )
    `);
  } catch { }
}
ensureFeatureTables();

/* ═══════════════════════════════════════════════════════════════════
   1. SIMILAR TENDERS
═══════════════════════════════════════════════════════════════════ */
router.get("/tenders/:id/similar", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const tenderId = Number(req.params.id);
    const companyId = req.companyId!;

    const current = await db.select().from(tendersTable)
      .where(and(eq(tendersTable.id, tenderId), eq(tendersTable.companyId, companyId)))
      .limit(1);
    if (!current[0]) return res.status(404).json({ error: "Not found" });

    const { sector, governmentEntity } = current[0];
    const orConds: any[] = [];
    if (sector) orConds.push(eq(tendersTable.sector, sector));
    if (governmentEntity) orConds.push(eq(tendersTable.governmentEntity, governmentEntity));

    const baseWhere = and(
      eq(tendersTable.companyId, companyId),
      sql`${tendersTable.id} != ${tenderId}`,
      orConds.length ? or(...orConds) : sql`TRUE`,
    );

    const similar = await db.select({
      id: tendersTable.id,
      title: tendersTable.title,
      sector: tendersTable.sector,
      governmentEntity: tendersTable.governmentEntity,
      status: tendersTable.status,
      deadline: tendersTable.deadline,
      hasAnalysis: tendersTable.hasAnalysis,
    }).from(tendersTable)
      .where(baseWhere)
      .orderBy(desc(tendersTable.createdAt))
      .limit(5);

    res.json(similar);
  } catch (err) { next(err); }
});

/* ═══════════════════════════════════════════════════════════════════
   2. AI CLARIFICATION QUESTIONS
═══════════════════════════════════════════════════════════════════ */
router.post("/tenders/:id/clarification-questions", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const tenderId = Number(req.params.id);
    const companyId = req.companyId!;
    const { language = "ar" } = req.body;

    const [tenderRows, analysisRows, docRows] = await Promise.all([
      db.select().from(tendersTable)
        .where(and(eq(tendersTable.id, tenderId), eq(tendersTable.companyId, companyId))).limit(1),
      db.select().from(analysesTable)
        .where(eq(analysesTable.tenderId, tenderId))
        .orderBy(desc(analysesTable.createdAt)).limit(1),
      db.select({ extractedText: tenderDocumentsTable.extractedText })
        .from(tenderDocumentsTable)
        .where(eq(tenderDocumentsTable.tenderId, tenderId)),
    ]);
    if (!tenderRows[0]) return res.status(404).json({ error: "Not found" });

    const { client, model } = await getClient();
    const docText = docRows.map(d => d.extractedText || "").filter(Boolean).join("\n\n---\n\n").slice(0, 5000);
    const analysisStr = analysisRows[0] ? JSON.stringify(analysisRows[0]).slice(0, 2000) : "";

    const systemAr = `أنت خبير في المناقصات الحكومية السعودية. بناءً على وثائق المناقصة وتحليلها، اقترح أسئلة توضيحية مهمة يجب توجيهها لجهة المناقصة. أرجع JSON فقط.`;
    const systemEn = `You are a Saudi government tender expert. Suggest important clarification questions to ask the contracting authority. Return JSON only.`;

    const userPrompt = `Tender: ${tenderRows[0].title}
Description: ${tenderRows[0].description || ""}
${docText ? `Documents:\n${docText}` : ""}
${analysisStr ? `Analysis:\n${analysisStr}` : ""}
Return JSON: { "questions": [{ "question": "string", "category": "technical|financial|legal|timeline|scope", "priority": "high|medium|low", "reason": "string" }] }
Generate 8-12 questions in ${language === "ar" ? "Arabic" : "English"}.`;

    const response = await client.chat.completions.create({
      model,
      messages: [
        { role: "system", content: language === "ar" ? systemAr : systemEn },
        { role: "user", content: userPrompt },
      ],
      response_format: { type: "json_object" },
      temperature: 0.4,
      max_tokens: 2000,
    });

    const parsed = JSON.parse(response.choices[0]?.message?.content || "{}");
    res.json(parsed.questions || []);
  } catch (err) { next(err); }
});

/* ═══════════════════════════════════════════════════════════════════
   3. DOCUMENT FULL-TEXT SEARCH
═══════════════════════════════════════════════════════════════════ */
router.get("/tenders/:id/doc-search", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const tenderId = Number(req.params.id);
    const companyId = req.companyId!;
    const q = (req.query.q as string || "").trim();
    if (!q) return res.json([]);

    const tender = await db.select({ id: tendersTable.id }).from(tendersTable)
      .where(and(eq(tendersTable.id, tenderId), eq(tendersTable.companyId, companyId))).limit(1);
    if (!tender[0]) return res.status(404).json({ error: "Not found" });

    const docs = await db.select().from(tenderDocumentsTable)
      .where(and(
        eq(tenderDocumentsTable.tenderId, tenderId),
        sql`LOWER(${tenderDocumentsTable.extractedText}) LIKE ${'%' + q.toLowerCase() + '%'}`,
      ));

    const results = docs.map(doc => {
      const text = doc.extractedText || "";
      const lower = text.toLowerCase();
      const qLower = q.toLowerCase();
      const snippets: { text: string; offset: number }[] = [];
      let idx = 0;
      while (snippets.length < 5) {
        const pos = lower.indexOf(qLower, idx);
        if (pos === -1) break;
        const start = Math.max(0, pos - 120);
        const end = Math.min(text.length, pos + q.length + 120);
        snippets.push({ text: text.slice(start, end), offset: pos });
        idx = pos + 1;
      }
      return { docId: doc.id, fileName: doc.fileName, matchCount: snippets.length, snippets };
    });

    res.json(results);
  } catch (err) { next(err); }
});

/* ═══════════════════════════════════════════════════════════════════
   4. TEAM NOTES (CRUD)
═══════════════════════════════════════════════════════════════════ */
router.get("/tenders/:id/notes", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const tenderId = Number(req.params.id);
    const tender = await db.select({ id: tendersTable.id }).from(tendersTable)
      .where(and(eq(tendersTable.id, tenderId), eq(tendersTable.companyId, req.companyId!))).limit(1);
    if (!tender[0]) return res.status(404).json({ error: "Not found" });
    const notes = await db.select().from(tenderNotesTable)
      .where(eq(tenderNotesTable.tenderId, tenderId))
      .orderBy(desc(tenderNotesTable.createdAt));
    res.json(notes);
  } catch (err) { next(err); }
});

router.post("/tenders/:id/notes", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const tenderId = Number(req.params.id);
    const { content, mentions } = req.body;
    if (!content?.trim()) return res.status(400).json({ error: "Content required" });
    const tender = await db.select({ id: tendersTable.id }).from(tendersTable)
      .where(and(eq(tendersTable.id, tenderId), eq(tendersTable.companyId, req.companyId!))).limit(1);
    if (!tender[0]) return res.status(404).json({ error: "Not found" });
    const authorName = `${req.dbUser!.firstName ?? ""} ${req.dbUser!.lastName ?? ""}`.trim() || req.dbUser!.email;
    const note = await db.insert(tenderNotesTable).values({
      tenderId, userId: req.dbUser!.id, authorName, content: content.trim(),
      mentions: mentions || [],
    }).returning();
    res.status(201).json(note[0]);
  } catch (err) { next(err); }
});

router.delete("/tenders/:id/notes/:noteId", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const noteId = Number(req.params.noteId);
    const note = await db.select().from(tenderNotesTable).where(eq(tenderNotesTable.id, noteId)).limit(1);
    if (!note[0]) return res.status(404).json({ error: "Not found" });
    if (note[0].userId !== req.dbUser!.id) return res.status(403).json({ error: "Forbidden" });
    await db.delete(tenderNotesTable).where(eq(tenderNotesTable.id, noteId));
    res.status(204).send();
  } catch (err) { next(err); }
});

/* ═══════════════════════════════════════════════════════════════════
   5. SUBMISSION CHECKLIST
═══════════════════════════════════════════════════════════════════ */
const DEFAULT_CHECKLIST = [
  { label: "العرض الفني",                              labelEn: "Technical Proposal",        category: "documents",   sortOrder: 1 },
  { label: "العرض المالي",                              labelEn: "Financial Proposal",         category: "documents",   sortOrder: 2 },
  { label: "الوثائق القانونية (السجل التجاري، الترخيص)", labelEn: "Legal Documents",             category: "legal",       sortOrder: 3 },
  { label: "شهادة الزكاة والضريبة",                     labelEn: "Zakat & Tax Certificate",    category: "legal",       sortOrder: 4 },
  { label: "شهادة التأمينات الاجتماعية (GOSI)",         labelEn: "GOSI Certificate",           category: "legal",       sortOrder: 5 },
  { label: "شهادة حسن السير والسلوك",                   labelEn: "Good Standing Certificate",  category: "legal",       sortOrder: 6 },
  { label: "الكفالة الابتدائية (إن وُجدت)",              labelEn: "Bid Bond (if required)",     category: "financial",   sortOrder: 7 },
  { label: "مراجعة متطلبات الامتثال",                   labelEn: "Review Compliance Checklist", category: "compliance",  sortOrder: 8 },
  { label: "رفع العرض على المنصة الرسمية (منصة اعتماد)", labelEn: "Submit on Etimad Platform",  category: "submission",  sortOrder: 9 },
];

router.get("/tenders/:id/checklist", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const tenderId = Number(req.params.id);
    const tender = await db.select({ id: tendersTable.id }).from(tendersTable)
      .where(and(eq(tendersTable.id, tenderId), eq(tendersTable.companyId, req.companyId!))).limit(1);
    if (!tender[0]) return res.status(404).json({ error: "Not found" });

    let items = await db.select().from(submissionChecklistTable)
      .where(eq(submissionChecklistTable.tenderId, tenderId))
      .orderBy(asc(submissionChecklistTable.sortOrder));

    if (items.length === 0) {
      const inserted = await db.insert(submissionChecklistTable)
        .values(DEFAULT_CHECKLIST.map(i => ({ ...i, tenderId })))
        .returning();
      items = inserted.sort((a, b) => a.sortOrder - b.sortOrder);
    }
    res.json(items);
  } catch (err) { next(err); }
});

router.patch("/tenders/:id/checklist/:itemId", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const { done, label, labelEn } = req.body;
    const updated = await db.update(submissionChecklistTable)
      .set({
        ...(done !== undefined ? { done } : {}),
        ...(label !== undefined ? { label } : {}),
        ...(labelEn !== undefined ? { labelEn } : {}),
        updatedAt: new Date(),
      })
      .where(eq(submissionChecklistTable.id, Number(req.params.itemId)))
      .returning();
    res.json(updated[0]);
  } catch (err) { next(err); }
});

router.post("/tenders/:id/checklist", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const tenderId = Number(req.params.id);
    const { label, labelEn, category } = req.body;
    if (!label) return res.status(400).json({ error: "Label required" });
    const tender = await db.select({ id: tendersTable.id }).from(tendersTable)
      .where(and(eq(tendersTable.id, tenderId), eq(tendersTable.companyId, req.companyId!))).limit(1);
    if (!tender[0]) return res.status(404).json({ error: "Not found" });
    const maxSort = await db.select({ m: sql<number>`COALESCE(MAX(sort_order),0)` })
      .from(submissionChecklistTable).where(eq(submissionChecklistTable.tenderId, tenderId));
    const item = await db.insert(submissionChecklistTable)
      .values({ tenderId, label, labelEn, category: category || "general", sortOrder: (maxSort[0]?.m || 0) + 1 })
      .returning();
    res.status(201).json(item[0]);
  } catch (err) { next(err); }
});

router.delete("/tenders/:id/checklist/:itemId", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    await db.delete(submissionChecklistTable)
      .where(eq(submissionChecklistTable.id, Number(req.params.itemId)));
    res.status(204).send();
  } catch (err) { next(err); }
});

/* ═══════════════════════════════════════════════════════════════════
   6. EXPORT ZIP BUNDLE
═══════════════════════════════════════════════════════════════════ */
router.get("/tenders/:id/export-bundle", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const tenderId = Number(req.params.id);
    const companyId = req.companyId!;
    const language = ((req.query.language as string) || "ar") as "ar" | "en";

    const [tenderRows, draftRows, companyRows, complianceRows] = await Promise.all([
      db.select().from(tendersTable).where(and(eq(tendersTable.id, tenderId), eq(tendersTable.companyId, companyId))).limit(1),
      db.select().from(proposalDraftsTable).where(eq(proposalDraftsTable.tenderId, tenderId)).limit(1),
      db.select().from(companiesTable).where(eq(companiesTable.id, companyId)).limit(1),
      db.select().from(complianceItemsTable).where(eq(complianceItemsTable.tenderId, tenderId)).orderBy(asc(complianceItemsTable.category)),
    ]);

    if (!tenderRows[0]) return res.status(404).json({ error: "Not found" });
    const draft = draftRows[0] as Record<string, any> | undefined;
    if (!draft) return res.status(400).json({ error: "Generate the proposal first before exporting" });

    const tenderTitle = tenderRows[0].title;
    const companyName = (companyRows[0] as any)?.name || "";

    const [techPdf, finPdf] = await Promise.all([
      generatePdfDocument(draft, tenderTitle, companyName, "technical", language),
      generatePdfDocument(draft, tenderTitle, companyName, "financial", language),
    ]);

    const isAr = language === "ar";
    const complianceCsv = [
      isAr ? "المتطلب,الفئة,الحالة,الملاحظات" : "Requirement,Category,Status,Notes",
      ...complianceRows.map(r =>
        `"${(r.requirement || "").replace(/"/g, '""')}","${r.category}","${r.status}","${(r.notes || "").replace(/"/g, '""')}"`
      ),
    ].join("\n");

    res.setHeader("Content-Type", "application/zip");
    res.setHeader("Content-Disposition", `attachment; filename="tender-${tenderId}-bundle.zip"`);

    const archive = archiver("zip", { zlib: { level: 6 } });
    archive.on("error", (err: Error) => { throw err; });
    archive.pipe(res as any);
    archive.append(techPdf, { name: isAr ? "العرض-الفني.pdf" : "technical-proposal.pdf" });
    archive.append(finPdf, { name: isAr ? "العرض-المالي.pdf" : "financial-proposal.pdf" });
    archive.append(Buffer.from("\uFEFF" + complianceCsv, "utf-8"), { name: isAr ? "مصفوفة-الامتثال.csv" : "compliance-matrix.csv" });
    await archive.finalize();
  } catch (err) { next(err); }
});

export default router;
